import { useEffect, useRef, useState } from "react";
import SearchFilter, { type SelectedFilter } from "./searchFilters";

import type { JobCategoryWithTags } from "@/types/db";
import type { LocationsResponse } from "@/lib/locations";

interface SearchFilterMobileProps {
  categoriesWithTags: JobCategoryWithTags[];
  locations: LocationsResponse;
  selectedFilters?: SelectedFilter[];
  onSelectFilter?: (filter: SelectedFilter) => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  activeCountry?: string;
  activeState?: string;
}

export default function SearchFilterMobile({
  categoriesWithTags,
  locations,
  selectedFilters = [],
  onSelectFilter,
  searchQuery = "",
  onSearchChange,
  activeCountry,
  activeState,
}: SearchFilterMobileProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const restoreFocusRafRef = useRef<number | null>(null);

  const activeCount =
    selectedFilters.length + (activeCountry ? 1 : 0) + (activeState ? 1 : 0);

  useEffect(() => {
    if (!isOpen) return;
    const dialog = dialogRef.current;
    if (!dialog) return;

    // Cancel any pending focus restore from a previous close so a rapid
    // close→open doesn't steal focus from the freshly opened dialog.
    if (restoreFocusRafRef.current !== null) {
      cancelAnimationFrame(restoreFocusRafRef.current);
      restoreFocusRafRef.current = null;
    }

    const focusableSelector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const getFocusables = () =>
      Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector));

    // Move focus into the dialog on open.
    getFocusables()[0]?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        return;
      }
      if (e.key !== "Tab") return;

      const focusables = getFocusables();
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      // Defer focus restore so the dialog is fully removed from the DOM
      // before the trigger receives focus — avoids edge-case focus loss
      // when React commits the unmount alongside this cleanup.
      restoreFocusRafRef.current = requestAnimationFrame(() => {
        restoreFocusRafRef.current = null;
        triggerRef.current?.focus();
      });
    };
  }, [isOpen]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="bg-polaris-orange fixed right-5 bottom-5 z-40 flex cursor-pointer items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-white shadow-lg ring-1 ring-black/5 transition-transform hover:scale-105 active:scale-95 lg:hidden"
        onClick={() => setIsOpen(true)}
        aria-label={
          activeCount > 0
            ? `Show filters (${activeCount} active)`
            : "Show filters"
        }
      >
        <svg
          className="size-5"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z"
            clipRule="evenodd"
          />
        </svg>
        Filters
        {activeCount > 0 && (
          <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs tabular-nums">
            {activeCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="mobile-filters-title"
          className="fixed inset-0 z-50 flex flex-col bg-white"
        >
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <h2
              id="mobile-filters-title"
              className="text-lg font-semibold tracking-tight text-gray-900"
            >
              Filters
            </h2>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus-visible:ring-2 focus-visible:ring-orange-300 focus-visible:outline-none"
              aria-label="Close filters"
            >
              <svg
                className="size-6"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            <SearchFilter
              categoriesWithTags={categoriesWithTags}
              locations={locations}
              selectedFilters={selectedFilters}
              onSelectFilter={onSelectFilter}
              searchQuery={searchQuery}
              onSearchChange={onSearchChange}
              activeCountry={activeCountry}
              activeState={activeState}
            />
          </div>

          <div className="border-t border-gray-200 bg-white p-4">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="bg-polaris-orange w-full rounded-lg py-3 font-semibold text-white transition-colors hover:bg-orange-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500"
            >
              {activeCount > 0
                ? `Apply ${activeCount} filter${activeCount === 1 ? "" : "s"}`
                : "Apply filters"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
